import { requireAnySystemPermission, requireOrgPermission } from '../../../../../../backend/features/permissions/shared';
import { LabelTypography } from '../../../../../../ui/components/LabelTypography';
import { OrganizationRoleSelect, OrganizationRoleSelectRequired, } from '../../../../../../ui/features/organizations/components/OrganizationsPanel/components/OrganizationRoleSelect';
import { OrganizationStatusSelect, OrganizationStatusSelectRequired, } from '../../../../../../ui/features/organizations/components/OrganizationsPanel/components/OrganizationStatusSelect';
import { OrganizationsSelection } from '../../../../../../ui/features/organizations/components/OrganizationsPanel/components/OrganizationsSelection';
import { useUserState } from '../../../../../../ui/hooks';
import { useContactManagementTranslation } from '../../../../../../ui/hooks/useContactManagementTranslation';
import { handleErrorDialog, showAlertDialog } from '@juvare/common-web-ui/DialogHelper';
import { NavigationContext } from '@juvare/common-web-ui/NavigationContext';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { Alert, Box, Button, Typography, useMediaQuery } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
const AccountMembershipUserComponent = (props, ref) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const navigationContext = React.useContext(NavigationContext);
    const settings = useSelector(getSettings);
    const { orgRoleMap, orgStatusMap } = settings;
    const userState = useUserState();
    const { t } = useContactManagementTranslation();
    const [error, setError] = React.useState();
    const [roleData, setRoleData] = React.useState((_b = (_a = props.data) === null || _a === void 0 ? void 0 : _a.orgRole) !== null && _b !== void 0 ? _b : {});
    const [statusData, setStatusData] = React.useState((_d = (_c = props.data) === null || _c === void 0 ? void 0 : _c.orgStatus) !== null && _d !== void 0 ? _d : {});
    const downSmScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const handleSubmit = (orgData) => {
        if (!requireAnySystemPermission(userState, 'manage-accounts') &&
            Object.keys(roleData).length === 0 &&
            Object.keys(statusData).length === 0) {
            setError(t('forms.accountMembershipUser.mustSelectMembership'));
            return Promise.reject();
        }
        const data = orgData !== null && orgData !== void 0 ? orgData : { orgRole: roleData, orgStatus: statusData };
        const savePromise = props
            .onSubmit(data)
            .then((response) => {
            if (props.onSubmitSuccess) {
                props.onSubmitSuccess(data, response);
            }
        })
            .catch((error) => {
            handleErrorDialog(error);
            throw error;
        });
        navigationContext.save(savePromise);
        return savePromise;
    };
    React.useImperativeHandle(ref, () => ({
        submit: () => handleSubmit(),
        getData: () => ({ orgRole: roleData, orgStatus: statusData }),
    }));
    const organizationIds = Array.from(new Set([...Object.keys(roleData), ...Object.keys(statusData)]));
    const getDefaultRole = () => {
        var _a;
        const [defaultRole] = (_a = Object.entries(orgRoleMap).find(([, role]) => !!role.default)) !== null && _a !== void 0 ? _a : [];
        return defaultRole;
    };
    const wizard = ((_e = props.formParams) === null || _e === void 0 ? void 0 : _e.page) === 'new' ||
        ((_f = props.formParams) === null || _f === void 0 ? void 0 : _f.page) === 'public-registration' ||
        ((_g = props.formParams) === null || _g === void 0 ? void 0 : _g.page) === 'login-completion';
    const handleChangeOrganizationIds = (newOrganizationIds) => {
        var _a, _b, _c, _d, _e;
        const newRoleData = Object.assign({}, roleData);
        const newStatusData = Object.assign({}, statusData);
        const userHasSystemPermissions = requireAnySystemPermission(userState, 'manage-accounts') &&
            requireAnySystemPermission(userState, 'manage-accounts-organizations');
        for (const orgId of organizationIds) {
            if (!newOrganizationIds.includes(orgId)) {
                if (requireOrgPermission(userState, orgId, 'manage-members') || userHasSystemPermissions) {
                    delete newRoleData[orgId];
                    delete newStatusData[orgId];
                }
                else if (requireOrgPermission(userState, orgId, 'view-and-transfer-members')) {
                    delete newRoleData[orgId];
                    delete newStatusData[orgId];
                }
                else if (((_a = props.formParams) === null || _a === void 0 ? void 0 : _a.id) === (userState === null || userState === void 0 ? void 0 : userState.id)) {
                    if ((((_b = props.data) === null || _b === void 0 ? void 0 : _b.orgRole) && props.data.orgRole[orgId]) || (((_c = props.data) === null || _c === void 0 ? void 0 : _c.orgStatus) && props.data.orgStatus[orgId])) {
                        newStatusData[orgId] = 'withdrawn';
                        delete newRoleData[orgId];
                    }
                    else {
                        delete newStatusData[orgId];
                    }
                }
            }
        }
        for (const orgId of newOrganizationIds) {
            if (!organizationIds.includes(orgId)) {
                if (requireOrgPermission(userState, orgId, 'manage-members') || userHasSystemPermissions) {
                    newRoleData[orgId] = (_d = getDefaultRole()) !== null && _d !== void 0 ? _d : 'user'; // TODO need to make 'user' and 'admin' as required roles, meaning they must always exist
                    newStatusData[orgId] = 'accepted';
                }
                else if (((_e = props.formParams) === null || _e === void 0 ? void 0 : _e.id) === (userState === null || userState === void 0 ? void 0 : userState.id)) {
                    newStatusData[orgId] = 'pending';
                }
            }
        }
        setRoleData(newRoleData);
        setStatusData(newStatusData);
        if (!wizard) {
            navigationContext.setChanged(t('common.saveChanges'), () => handleSubmit({
                orgRole: newRoleData,
                orgStatus: newStatusData,
            }));
        }
        if (props.onChanged) {
            props.onChanged(true, () => handleSubmit({ orgRole: newRoleData, orgStatus: newStatusData }));
        }
    };
    const handleChangeRoleStatus = (orgId, role, status) => {
        var _a, _b, _c;
        const newRoleData = Object.assign({}, roleData);
        const newStatusData = Object.assign({}, statusData);
        if (status) {
            if (role && roleData[orgId] !== role && !((_a = orgStatusMap[status]) === null || _a === void 0 ? void 0 : _a.requireOrgRole)) {
                const applicableStatuses = Object.values(orgStatusMap)
                    .filter((s) => s.requireOrgRole)
                    .map((s) => `"${s.label}"`);
                showAlertDialog({
                    title: t('forms.orgMembers.cannotChangeRole', {
                        statuses: applicableStatuses.join(', '),
                        count: applicableStatuses.length,
                    }),
                });
                return;
            }
        }
        if (role) {
            newRoleData[orgId] = role;
        }
        else {
            delete newRoleData[orgId];
        }
        if (status) {
            newStatusData[orgId] = status;
        }
        else {
            delete newStatusData[orgId];
        }
        if (status && statusData[orgId] !== status) {
            if ((_b = orgStatusMap[status]) === null || _b === void 0 ? void 0 : _b.requireOrgRole) {
                if (!roleData[orgId]) {
                    newRoleData[orgId] = (_c = getDefaultRole()) !== null && _c !== void 0 ? _c : 'user';
                }
            }
            else {
                delete newRoleData[orgId];
            }
        }
        setRoleData(newRoleData);
        setStatusData(newStatusData);
        if (!wizard) {
            navigationContext.setChanged(t('common.saveChanges'), () => handleSubmit({
                orgRole: newRoleData,
                orgStatus: newStatusData,
            }));
        }
        if (props.onChanged) {
            props.onChanged(true, () => handleSubmit({ orgRole: newRoleData, orgStatus: newStatusData }));
        }
    };
    return (React.createElement(Box, null,
        error && (React.createElement(Box, { sx: { mb: 2 } },
            React.createElement(Alert, { severity: 'error' },
                React.createElement(Typography, { sx: { mb: 2 } }, t('common.formioErrorTitle')),
                error))),
        React.createElement(OrganizationsSelection, { accountId: props.accountId, showSelectAllChildren: true, organizationIds: organizationIds, staticDialogFilters: {
                visible: ((_h = props.formParams) === null || _h === void 0 ? void 0 : _h.id) === (userState === null || userState === void 0 ? void 0 : userState.id),
                orgPermissions: ((_j = props.formParams) === null || _j === void 0 ? void 0 : _j.id) !== (userState === null || userState === void 0 ? void 0 : userState.id) ? ['manage-members', 'view-and-transfer-members'] : undefined,
                permission: ((_k = props.formParams) === null || _k === void 0 ? void 0 : _k.id) === (userState === null || userState === void 0 ? void 0 : userState.id) ? 'member' : undefined,
            }, onChangeOrganizationIds: handleChangeOrganizationIds, renderOrganization: (organization) => {
                var _a;
                const targetOrgId = organization.id.split('_')[0];
                const manage = requireOrgPermission(userState, targetOrgId, 'manage-members') ||
                    (requireAnySystemPermission(userState, 'manage-accounts') &&
                        requireAnySystemPermission(userState, 'manage-accounts-organizations'));
                const currentOrgStatus = ((_a = props.data) === null || _a === void 0 ? void 0 : _a.orgStatus) && props.data.orgStatus[targetOrgId];
                return wrapForMobileIfTrue(downSmScreen, React.createElement(React.Fragment, null,
                    React.createElement(LabelTypography, { sx: [{ flexGrow: 1 }, downSmScreen ? { pb: 1 / 2 } : {}], color: organization.disabled ? 'textSecondary' : 'initial', "aria-disabled": organization.disabled }, organization.name),
                    organizationIds.includes(targetOrgId) && !manage && (React.createElement(React.Fragment, null,
                        React.createElement(WrapperOrganizationRoleSelect, null, currentOrgStatus ? (React.createElement(OrganizationStatusSelectRequired, { variant: 'standard', fullWidth: true, limitValuesTo: currentOrgStatus
                                ? [currentOrgStatus, currentOrgStatus !== 'withdrawn' ? 'withdrawn' : 'pending']
                                : ['pending'], status: statusData[targetOrgId], onChange: (status) => handleChangeRoleStatus(targetOrgId, roleData[targetOrgId], status) })) : (React.createElement(OrganizationStatusSelect, { variant: 'standard', fullWidth: true, limitValuesTo: ['pending'], status: statusData[targetOrgId], onChange: (status) => handleChangeRoleStatus(targetOrgId, roleData[targetOrgId], status) }))),
                        React.createElement(WrapperOrganizationRoleSelect, null,
                            React.createElement(OrganizationRoleSelect, { variant: 'standard', fullWidth: true, disabled: true, role: roleData[targetOrgId], onChange: (role) => handleChangeRoleStatus(targetOrgId, role, statusData[targetOrgId]) })))),
                    organizationIds.includes(targetOrgId) && manage && (React.createElement(React.Fragment, null,
                        React.createElement(WrapperOrganizationRoleSelect, null,
                            React.createElement(OrganizationStatusSelectRequired, { variant: 'standard', fullWidth: true, status: statusData[targetOrgId], onChange: (status) => handleChangeRoleStatus(targetOrgId, roleData[targetOrgId], status) })),
                        React.createElement(WrapperOrganizationRoleSelect, null,
                            React.createElement(OrganizationRoleSelectRequired, { variant: 'standard', fullWidth: true, role: roleData[targetOrgId], onChange: (role) => handleChangeRoleStatus(targetOrgId, role, statusData[targetOrgId]) }))))));
            } }),
        props.showSubmitButton && (React.createElement(Box, { pt: 2, sx: { display: 'flex', justifyContent: 'flex-end' } },
            React.createElement(Button, { variant: 'contained', color: 'primary', onClick: () => handleSubmit() }, t('common.save'))))));
};
function wrapForMobileIfTrue(condition, component) {
    return condition ? (React.createElement(Box, { sx: {
            pt: '4px',
            pb: '4px',
            width: '100%',
        } }, component)) : (component);
}
const WrapperOrganizationRoleSelect = (props) => {
    const downSmScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    return React.createElement(Box, { sx: [{ width: 200, ml: 2 }, downSmScreen ? { ml: 0, pb: 1 } : {}] }, props.children);
};
export const AccountMembershipUser = React.forwardRef(AccountMembershipUserComponent);
